/**
 * Unified Logging Service
 * Sends logs to backend /api/logs endpoint for aggregation.
 */
const LOG_LEVELS = {
    INFO: 'INFO',
    WARN: 'WARN',
    ERROR: 'ERROR',
    DEBUG: 'DEBUG',
};

// Generate a session ID for this page load to correlate frontend events
const SESSION_ID = 'sess_' + Math.random().toString(36).substring(2, 10);

const logger = {
    info: (message, context = {}) => {
        log(LOG_LEVELS.INFO, message, context);
    },
    warn: (message, context = {}) => {
        log(LOG_LEVELS.WARN, message, context);
    },
    error: (message, context = {}) => {
        log(LOG_LEVELS.ERROR, message, context);
    },
    debug: (message, context = {}) => {
        if (process.env.NODE_ENV !== 'production') {
            log(LOG_LEVELS.DEBUG, message, context);
        }
    },
    performance: (label, durationMs, metadata = {}) => {
        log(LOG_LEVELS.INFO, `Performance: ${label}`, {
            ...metadata,
            duration_ms: durationMs,
            event_type: 'performance'
        });
    }
};

function log(level, message, context) {
    const timestamp = new Date().toISOString();

    // Auto-enrichment
    const userId = localStorage.getItem('userId') || '';
    const traceId = context.traceId || ''; // passed manually or empty

    const logEntry = {
        timestamp,
        level,
        message,
        context, // keep raw context nested

        // Promoted fields for backend indexing
        userId,
        session_id: SESSION_ID,
        trace_id: traceId,
        userAgent: navigator.userAgent,
        url: window.location.href,

        // Flatten critical performance fields if present
        duration_ms: context.duration_ms,
    };

    // 1. Console Output
    const style = getLogStyle(level);
    const consoleMsg = level === 'ERROR' ? message : `[${level}] ${message}`;

    // Avoid double logging if it's a console.error loop
    if (context.__fromConsole) return;

    if (level === LOG_LEVELS.ERROR) {
        console.error(consoleMsg, context);
    } else if (level === LOG_LEVELS.WARN) {
        console.warn(consoleMsg, context);
    } else {
        console.log(`%c${consoleMsg}`, style, context);
    }

    // 2. Send to backend
    sendToLogServer(logEntry);
}

function sendToLogServer(entry) {
    // Basic formatting or filtering if needed
    const API_URL = process.env.REACT_APP_API_URL || ''; // relative path if proxy, or absolute

    // Fire and forget - don't await
    // We use beacon if available for ensuring reliability on unload, otherwise fetch
    const payload = JSON.stringify(entry);

    if (navigator.sendBeacon && (entry.level === LOG_LEVELS.ERROR || entry.event_type === 'performance')) {
        // Use beacon for errors/perf to ensure delivery
        // Blob is safer for CORS
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(`${API_URL}/api/logs`, blob);
    } else {
        fetch(`${API_URL}/api/logs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: payload,
            keepalive: true,
        }).catch(err => {
            // Fallback to console if backend is down, to avoid infinite loop
            // Use a flag to prevent recursive logging
            console.warn('Failed to send log to backend:', err);
        });
    }
}

function getLogStyle(level) {
    switch (level) {
        case LOG_LEVELS.ERROR: return 'color: red; font-weight: bold;';
        case LOG_LEVELS.WARN: return 'color: orange; font-weight: bold;';
        case LOG_LEVELS.INFO: return 'color: #00d8ff; font-weight: bold;';
        case LOG_LEVELS.DEBUG: return 'color: #888;';
        default: return 'color: inherit;';
    }
}

// --- Global Error Handlers ---

window.onerror = function (msg, url, line, col, error) {
    logger.error('Uncaught Exception: ' + msg, {
        source: 'window.onerror',
        url,
        line,
        col,
        stack: error ? error.stack : ''
    });
};

window.onunhandledrejection = function (event) {
    logger.error('Unhandled Promise Rejection', {
        source: 'window.onunhandledrejection',
        reason: event.reason ? (event.reason.stack || event.reason) : 'Unknown'
    });
};

export default logger;
